import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  ArrowLeft, ArrowRight, Compass, Heart, MessagesSquare, Users, FilePlus2,
  ShieldCheck, Sparkles, ClipboardCheck, HandHeart, Route, ShieldAlert,
  Building2, Search, Star, FileCheck2, Send, UserCheck, Loader2,
} from "lucide-react";

const CANDIDATE_ICONS = [ClipboardCheck, Compass, HandHeart, Heart, MessagesSquare, Users, Route, FilePlus2];
const EMPLOYER_ICONS = [Building2, Search, Sparkles, Star, Send, MessagesSquare, FileCheck2];

export default function SaFunkarDet() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const isEmployer = user?.role === "employer" || params.get("for") === "arbetsgivare";
  const employerLoggedIn = user?.role === "employer" || user?.role === "admin";
  const backTo = isEmployer ? (employerLoggedIn ? "/foretag" : "/") : (user ? "/app" : "/");

  const [content, setContent] = useState(null);
  useEffect(() => {
    api.get("/guide-content").then((r) => setContent(r.data)).catch(() => setContent({}));
  }, []);

  if (content === null) {
    return <div className="hamn-bg min-h-screen flex items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>;
  }

  const c = (isEmployer ? content.employer : content.candidate) || {};
  const steps = c.steps || [];
  const icons = isEmployer ? EMPLOYER_ICONS : CANDIDATE_ICONS;

  return (
    <div className="hamn-bg min-h-screen">
      <header className="sticky top-0 z-30 border-b border-border bg-white/90 backdrop-blur-xl">
        <div className="mx-auto max-w-4xl px-4 py-4 flex items-center justify-between gap-3">
          <Link to={backTo} className="flex items-center gap-2.5 min-w-0" data-testid="guide-home-link">
            <img src="/hamn-logo.png" alt="HAMN" className="h-9 w-9 rounded-lg object-cover shrink-0" />
            <span className="font-head font-bold text-primary whitespace-nowrap">{isEmployer ? "HAMN Match" : "HAMN CV Studio"}</span>
          </Link>
          <Link to={backTo} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1.5" data-testid="guide-back-link">
            <ArrowLeft className="h-4 w-4" /> Tillbaka
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-10 md:py-16" data-testid="sa-funkar-det-page">
        <div className="flex items-center gap-2 text-primary mb-2"><Sparkles className="h-5 w-5" /><span className="text-sm font-medium">Så funkar det{isEmployer ? " – för arbetsgivare" : ""}</span></div>
        <h1 className="font-head font-bold text-3xl sm:text-4xl lg:text-5xl text-primary">{c.heading}</h1>
        <p className="text-base text-muted-foreground mt-4 max-w-2xl leading-relaxed">{c.intro}</p>

        <Card className="rounded-2xl border-primary/25 bg-primary/[0.04] p-5 sm:p-6 mt-8 flex items-start gap-3" data-testid="guide-ai-promise">
          <ShieldCheck className="h-6 w-6 text-primary shrink-0 mt-0.5" />
          <div>
            <div className="font-head font-semibold">{c.ai_card_title}</div>
            <p className="text-sm text-muted-foreground mt-1">{c.ai_card_text}</p>
          </div>
        </Card>

        <h2 className="font-head font-semibold text-xl mt-10 mb-4">{c.steps_heading}</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          {steps.map((s, i) => {
            const Icon = icons[i] || Sparkles;
            return (
              <Card key={i} className="rounded-2xl border-border p-5">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary mb-3"><Icon className="h-5 w-5" /></div>
                <div className="font-head font-semibold">{s.title}</div>
                <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{s.text}</p>
              </Card>
            );
          })}
        </div>

        {isEmployer ? (
          <>
            <Card className="rounded-2xl border-primary/25 bg-primary/[0.04] p-6 mt-8 flex items-start gap-3" data-testid="guide-employer-privacy">
              <UserCheck className="h-6 w-6 text-primary shrink-0 mt-0.5" />
              <div>
                <div className="font-head font-semibold">{c.privacy_title}</div>
                <p className="text-sm text-muted-foreground mt-1">{c.privacy_text}</p>
              </div>
            </Card>

            <Card className="rounded-2xl border-accent/25 bg-accent/[0.06] p-6 mt-4 flex items-start gap-3" data-testid="guide-employer-tip">
              <FileCheck2 className="h-6 w-6 text-accent shrink-0 mt-0.5" />
              <div>
                <div className="font-head font-semibold">{c.tip_title}</div>
                <p className="text-sm text-muted-foreground mt-1">{c.tip_text}</p>
              </div>
            </Card>
          </>
        ) : (
          <>
            <Card className="rounded-2xl border-primary/25 bg-primary/[0.04] p-6 mt-8 flex items-start gap-3" data-testid="guide-vagledning-tip">
              <Route className="h-6 w-6 text-primary shrink-0 mt-0.5" />
              <div>
                <div className="font-head font-semibold">{c.vagledning_tip_title}</div>
                <p className="text-sm text-muted-foreground mt-1">{c.vagledning_tip_text}</p>
              </div>
            </Card>

            <Card className="rounded-2xl border-border p-6 mt-4 flex items-start gap-3" data-testid="guide-review-tip">
              <ClipboardCheck className="h-6 w-6 text-primary shrink-0 mt-0.5" />
              <div>
                <div className="font-head font-semibold">{c.review_tip_title}</div>
                <p className="text-sm text-muted-foreground mt-1">{c.review_tip_text}</p>
              </div>
            </Card>
          </>
        )}

        <Card className="rounded-2xl border-primary/25 bg-primary/[0.04] p-6 mt-4 flex items-start gap-3" data-testid="guide-ai-transparency">
          <Sparkles className="h-6 w-6 text-primary shrink-0 mt-0.5" />
          <div>
            <div className="font-head font-semibold">{c.transparency_title}</div>
            <p className="text-sm text-muted-foreground mt-1">
              {c.transparency_text}{" "}
              Läs mer i vår <Link to="/integritetspolicy" className="text-primary font-medium hover:underline">integritetspolicy</Link>.
            </p>
          </div>
        </Card>

        {!isEmployer && (
          <Card className="rounded-2xl border-amber-300 bg-amber-50 p-6 mt-4 flex items-start gap-3" data-testid="guide-pnr-tip">
            <ShieldAlert className="h-6 w-6 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <div className="font-head font-semibold text-amber-900">{c.pnr_title}</div>
              <p className="text-sm text-amber-900 mt-1">{c.pnr_text}</p>
            </div>
          </Card>
        )}

        <div className="mt-10 flex flex-wrap gap-3">
          {isEmployer ? (
            employerLoggedIn ? (
              <Link to="/foretag"><Button size="lg" className="rounded-full h-12" data-testid="guide-cta-employer">Till HAMN Match <ArrowRight className="h-4 w-4 ml-2" /></Button></Link>
            ) : (
              <>
                <Link to="/foretag/registrera"><Button size="lg" className="rounded-full h-12" data-testid="guide-cta-employer-register">Skapa företagskonto <ArrowRight className="h-4 w-4 ml-2" /></Button></Link>
                <Link to="/foretag/logga-in"><Button size="lg" variant="outline" className="rounded-full h-12" data-testid="guide-cta-employer-login">Logga in</Button></Link>
              </>
            )
          ) : user ? (
            <Link to="/app/profil"><Button size="lg" className="rounded-full h-12" data-testid="guide-cta-profile">Fortsätt min profil <ArrowRight className="h-4 w-4 ml-2" /></Button></Link>
          ) : (
            <>
              <Link to="/register"><Button size="lg" className="rounded-full h-12" data-testid="guide-cta-register">Kom igång gratis <ArrowRight className="h-4 w-4 ml-2" /></Button></Link>
              <Link to="/login"><Button size="lg" variant="outline" className="rounded-full h-12" data-testid="guide-cta-login">Logga in</Button></Link>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
